import { useEffect, useReducer, useRef, useState } from 'react';
import Slider from 'react-slick';
import { useLocalStorage } from 'providers';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { OnboardingData, ReferralCodeErrorType } from 'models';
import { sendMarketingPreference, sendSurveyData, sendReferralCode } from 'services/loyaltyGateway';
import isValidReferralCode from 'helpers/isValidReferralCode';
import { slideReducer, useOnboardingSlides } from './OnboardingSlides.data';

import {
  Wrapper,
  Slides,
  StyledButton,
  Title,
  SubTitle,
  PageNumber,
  Slide,
  Header,
  HeaderButton,
  OnboardingContent,
  Content,
  Footer,
} from './OnboardingSlides.styles';

const referralCodeErrorText: Record<ReferralCodeErrorType, string> = {
  length: 'The referral code should contain 8 characters. Please check and try again.',
  alphanumeric:
    'The referral code should only contain letters and numbers. Please check and try again.',
};

interface OnboardingSlidesProps {
  onDone: () => void;
}

const initialButtonState = {
  foodInterests: true,
  dateOfBirth: true,
  location: true,
  referAFriend: false,
  marketing: true,
};

const OnboardingSlides = ({ onDone }: OnboardingSlidesProps) => {
  const { getFromStorage, removeFromStorage } = useLocalStorage();
  const magicLinkReferralCode = getFromStorage<string>('storedReferralCode') || '';

  useEffect(() => {
    pushAnalyticsEvent({ event: 'onboardingView' });
  }, []);

  const initialOnboardingData: OnboardingData = {
    surveyAnswers: {},
    referralCode: magicLinkReferralCode,
    referralCodeError: null,
    buttonState: initialButtonState,
  };

  const [onboardingData, setOnboardingData] = useReducer(slideReducer, initialOnboardingData);
  const [activeStep, setActiveStep] = useState<number>(0);

  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    sliderRef.current?.slickGoTo(activeStep);
  }, [activeStep]);

  const slides = useOnboardingSlides();
  const { shouldValidate, shouldSubmit } = slides[activeStep];

  const buttonText = activeStep === 0 ? 'Continue' : 'Next';
  const pagination = `${activeStep + 1} / ${slides.length}`;

  const { marketingOptIn, surveyAnswers, referralCode, referralCodeError, buttonState } =
    onboardingData;

  if (referralCodeError && !referralCodeError.text) {
    setOnboardingData({
      type: 'setReferralCodeError',
      data: {
        referralCodeError: {
          ...referralCodeError,
          text: referralCodeErrorText[referralCodeError.type],
        },
      },
    });
  }

  const submitResults = () => {
    if (marketingOptIn !== undefined) {
      sendMarketingPreference(marketingOptIn, true);
      pushAnalyticsEvent({
        event: 'marketingPreference',
        optedIn: marketingOptIn,
        location: 'Onboarding',
      });
    }

    if (referralCode) {
      sendReferralCode(referralCode);
      pushAnalyticsEvent({ event: 'addedReferralCode' });
      removeFromStorage('storedReferralCode');
    }

    const answers = Object.values(surveyAnswers).filter(
      ({ choiceIDs, value }) => choiceIDs?.length || value
    );

    if (!answers.length) pushAnalyticsEvent({ event: 'surveySkipped' });
    if (answers.length) sendSurveyData(answers);

    onDone();
  };

  const nextSlide = () =>
    shouldSubmit ? submitResults() : setActiveStep((prevStep) => prevStep + 1);

  const validateReferralCode = () => {
    if (referralCode && !isValidReferralCode(referralCode)) {
      const updatedButtonState = { ...buttonState, referAFriend: true };

      setOnboardingData({
        type: 'setReferralCodeError',
        data: { referralCodeError: { type: 'length' } },
      });

      setOnboardingData({
        type: 'setButtonState',
        data: { buttonState: updatedButtonState },
      });

      return;
    }

    nextSlide();
  };

  const handleOnNext = () => {
    pushAnalyticsEvent({ event: 'onboardingNext', title: slides[activeStep].id });
    return shouldValidate ? validateReferralCode() : nextSlide();
  };

  const handleOnBack = () => setActiveStep((prevStep) => prevStep - 1);

  const handleOnSkip = () => {
    pushAnalyticsEvent({ event: 'onboardingSkip', title: slides[activeStep].id });
    (shouldSubmit ? submitResults : nextSlide)();
  };

  const handleButtonState = (step: number) => {
    const { foodInterests, dateOfBirth, location, referAFriend, marketing } =
      buttonState || initialButtonState;

    const isDisabled: Record<number, boolean> = {
      1: foodInterests,
      2: dateOfBirth,
      3: location,
      4: referAFriend,
      5: marketing,
    };

    return isDisabled[step];
  };

  const showBackButton = activeStep >= 1;
  const showSkipButton = activeStep >= 1 && slides[activeStep].id !== 'Marketing Preferences';

  return (
    <Wrapper>
      <Header>
        {showBackButton && <HeaderButton onClick={handleOnBack}>Back</HeaderButton>}
        {showSkipButton && <HeaderButton onClick={handleOnSkip}>Skip</HeaderButton>}
      </Header>

      <Slides ref={sliderRef}>
        {slides.map(({ title, subtitle, component: Component }, idx) => (
          <Slide key={title} aria-label={`Onboarding slide ${idx + 1}`}>
            <Content>
              <Title variant="h2">{title}</Title>
              <SubTitle variant="h5">{subtitle}</SubTitle>
            </Content>

            <OnboardingContent>
              <Component onboardingData={onboardingData} setOnboardingData={setOnboardingData} />
            </OnboardingContent>
          </Slide>
        ))}
      </Slides>

      <Footer>
        <PageNumber variant="body1">{pagination}</PageNumber>
        <StyledButton
          disabled={handleButtonState(activeStep)}
          onClick={handleOnNext}
          fullWidth
          variant="contained"
          disableRipple
        >
          {buttonText}
        </StyledButton>
      </Footer>
    </Wrapper>
  );
};

export default OnboardingSlides;
